import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, FileDrop, Select, toast } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink } from "lucide-react";
import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  BomClient,
  PART_FILE_KINDS,
  PartFileClient,
  refusalMessage,
  revisionLabel,
  type BomDTO,
  type PartFileDTO,
  type PartFileKind,
} from "../../api/manufacturing";
import { bomStatusLabel } from "./PartBomTab";

/** What each kind is called on screen. */
export function partFileKindLabel(t: TFunction, kind: PartFileKind): string {
  switch (kind) {
    case "DRAWING":
      return t("part.files.kind.drawing", "Drawing");
    case "CAD_SOURCE":
      return t("part.files.kind.cad", "CAD");
    case "CNC_PROGRAM":
      return t("part.files.kind.cnc", "CNC program");
    case "EXPLODED_VIEW":
      return t("part.files.kind.exploded", "Exploded view");
    case "ASSEMBLY_GUIDE":
      return t("part.files.kind.guide", "Assembly guide");
    case "PHOTO":
      return t("part.files.kind.photo", "Photo");
    default:
      return t("part.files.kind.other", "Other");
  }
}

/** Bytes as people read them: 820 KB, 14.2 MB. */
function fileSize(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Drawings, CAD, exploded views and guides on one or more parts.
 *
 * <p>On a part page it is that part's files, with an upload zone. On a
 * product it is the files of the product's part and of its components, with
 * uploads going to the product's own part. A file opens through a link the
 * server signs for a few minutes: a PDF or an image shows in a new tab, any
 * other format (DWG, STEP, SolidWorks…) downloads for the CAD software.</p>
 *
 * <p>Each file says what it is and which revision of its part's bill it goes
 * with, picked from the part's revisions (older files carry the design
 * office's own mark, R2, SIGM, shown until one is picked). The current DRAWING is the
 * one the lot travellers print, and the current EXPLODED_VIEW the one on the
 * production sheet: uploading a new one makes it current, and an older one
 * can be made current again.</p>
 */
export function PartFilesPanel({
  parts,
  uploadPartId,
  productId,
  uploadToProduct,
  readOnly,
}: {
  /** The parts whose files are listed, with their numbers for the Part column. */
  parts: Array<{ id: number; number: string }>;
  /** Where uploads go; none = no upload zone. */
  uploadPartId: number | null;
  /** A product whose own files (0069: exploded view, general drawings) are listed too. */
  productId?: number | null;
  /** Uploads go to the product rather than to a part. */
  uploadToProduct?: boolean;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new PartFileClient(http), [http]);
  const [uploading, setUploading] = useState(false);
  // "" lets the server decide: a drawing for an image or a PDF, CAD for the rest.
  const [uploadKind, setUploadKind] = useState<PartFileKind | "">("");
  // "" = the file goes with no revision in particular.
  const [uploadBomId, setUploadBomId] = useState("");

  const ids = useMemo(() => [...new Set(parts.map((part) => part.id))].sort((a, b) => a - b), [parts]);
  const numberOf = useMemo(() => new Map(parts.map((part) => [part.id, part.number])), [parts]);
  const queryKey = ["manufacturing", "part-file", ids, productId ?? null] as const;

  const { data, isLoading } = useQuery({
    queryKey,
    // Every page: a product sharing its family's Inventor model carries hundreds
    // of files, and its components' drawings come on top.
    queryFn: async () => {
      const files: PartFileDTO[] = [];
      if (productId != null) {
        files.push(...(await client.find({ page: 0, size: 1000, filters: { product_id: [String(productId)] } })).content);
      }
      for (let page = 0; ids.length > 0; page++) {
        const { content } = await client.find({ page, size: 1000, filters: { part_id: ids.map(String) } });
        files.push(...content);
        if (content.length < 1000) break;
      }
      return files;
    },
    enabled: ids.length > 0 || productId != null,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["manufacturing", "part-file"] });

  // The listed parts' revisions, for the revision pickers. Under the BOM tab's
  // key, so a revision made there shows up here.
  const boms = useMemo(() => new BomClient(http), [http]);
  const { data: revisions } = useQuery({
    queryKey: ["manufacturing", "bom", "of-parts", ids],
    queryFn: async () => {
      const found: BomDTO[] = [];
      for (let page = 0; ; page++) {
        const { content } = await boms.find({ page, size: 1000, filters: { part_id: ids.map(String) } });
        found.push(...content);
        if (content.length < 1000) break;
      }
      return found;
    },
    enabled: ids.length > 0,
    staleTime: 60_000,
  });
  const revisionById = useMemo(() => new Map((revisions ?? []).map((bom) => [bom.id, bom])), [revisions]);
  const revisionOptions = (partId: number | null) => [
    { value: "", label: t("part.files.revision.none", "No revision") },
    ...(revisions ?? [])
      .filter((bom) => bom.part_id === partId)
      .map((bom) => ({ value: String(bom.id), label: `${revisionLabel(bom.created_at, bom.revision)}, ${bomStatusLabel(bom.status, t)}` })),
  ];
  // A file's revision: the bill revision it goes with, else the mark it came with.
  const revisionOf = (row: PartFileDTO) => {
    const bom = row.bom_id == null ? undefined : revisionById.get(row.bom_id);
    return bom ? revisionLabel(bom.created_at, bom.revision) : (row.revision ?? "");
  };

  const upload = async (files: File[]) => {
    const toProduct = uploadToProduct && productId != null;
    if ((!toProduct && uploadPartId == null) || files.length === 0) return;
    setUploading(true);
    let done = 0;
    try {
      for (const file of files) {
        const kind = uploadKind === "" ? undefined : uploadKind;
        if (toProduct) await client.uploadToProduct(productId, file, { kind });
        else await client.upload(uploadPartId!, file, { kind, bomId: uploadBomId === "" ? null : Number(uploadBomId) });
        done++;
      }
      toast.success(t("part.files.uploaded", "{{n}} files uploaded", { n: done, count: done }));
    } catch (failure) {
      // Say which file stopped it and that the ones before it are in: they
      // stay listed, and dropping the whole batch again would duplicate them.
      toast.error(
        t("part.files.upload-stopped", "{{name}}: {{reason}} ({{count}} uploaded before it)", {
          name: files[done]?.name ?? "",
          reason: refusalMessage(failure, t("part.files.upload-failed", "Upload failed")),
          count: done,
        }),
      );
    } finally {
      setUploading(false);
      await refresh();
    }
  };

  const makeCurrent = async (file: PartFileDTO) => {
    try {
      await client.makeCurrent(file.id);
      await refresh();
      toast.success(t("part.files.now-current", "{{name}} is now the current one", { name: file.file_name }));
    } catch (failure) {
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    }
  };

  // The tab opens before the link is fetched, so a popup blocker sees a click.
  const open = async (file: PartFileDTO) => {
    const tab = window.open("", "_blank");
    try {
      const url = await client.link(file.id);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (failure) {
      tab?.close();
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    }
  };

  const cols = useMemo(() => {
    const c = defineColumns<PartFileDTO>();
    return [
      c.custom(
        "file_name",
        (row) => row.file_name,
        (row) => (
          <button type="button" className="text-left text-accent hover:underline" onClick={() => void open(row)}>
            {row.file_name}
          </button>
        ),
        { headerKey: "part.files.name", priority: 1 },
      ),
      c.text("kind", (row) => partFileKindLabel(t, row.kind), {
        header: t("part.files.kind", "Kind"),
        filter: "select",
        filterOptions: PART_FILE_KINDS.map((kind) => ({ value: partFileKindLabel(t, kind), label: partFileKindLabel(t, kind) })),
        priority: 1,
      }),
      c.text("revision", revisionOf, { header: t("part.files.revision", "Revision"), priority: 2 }),
      c.custom(
        "current",
        (row) => (row.is_current ? 1 : 0),
        (row) =>
          row.is_current ? (
            <Badge variant="success" size="sm">
              {t("part.files.current", "current")}
            </Badge>
          ) : null,
        { header: t("part.files.current-column", "In use"), priority: 2 },
      ),
      c.text("part", (row) => (row.part_id == null ? t("part.files.of-product", "The product") : (numberOf.get(row.part_id) ?? "")), {
        headerKey: "column.part.number",
        filter: "select",
        priority: 2,
        defaultVisible: ids.length > 1 || productId != null,
      }),
      c.text("size", (row) => fileSize(row.file_size), { headerKey: "part.files.size", filter: "none", priority: 3 }),
      c.text("created_at", (row) => row.created_at?.slice(0, 10) ?? "", { headerKey: "part.files.uploaded-on", filter: "daterange", priority: 3 }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 2 }),
    ];
    // open and revisionOf are stable enough: they close over the client and revisionById.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOf, ids.length, productId, revisionById, t]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {!readOnly && (uploadPartId != null || (uploadToProduct && productId != null)) && (
        <div className="flex flex-col gap-2 border-b border-line bg-surface px-4 py-3 lg:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label={t("part.files.kind", "Kind")}
              value={uploadKind}
              className="h-8 w-48"
              options={[
                { value: "", label: t("part.files.kind.auto", "Kind: automatic") },
                ...PART_FILE_KINDS.map((kind) => ({ value: kind, label: partFileKindLabel(t, kind) })),
              ]}
              onChange={(event) => setUploadKind(event.target.value as PartFileKind | "")}
            />
            {!(uploadToProduct && productId != null) && (
              <Select
                aria-label={t("part.files.revision", "Revision")}
                value={uploadBomId}
                className="h-8 w-64"
                options={revisionOptions(uploadPartId)}
                onChange={(event) => setUploadBomId(event.target.value)}
              />
            )}
          </div>
          <FileDrop
            multiple
            disabled={uploading}
            onFiles={(files) => void upload(files)}
            hint={
              uploading
                ? t("part.files.uploading", "Uploading…")
                : t("part.files.hint", "Drop drawings and models here: PDF, DWG, DXF, STEP, SolidWorks… PDFs and images open in the browser, other files download.")
            }
          />
        </div>
      )}
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading && ids.length > 0}
        emptyMessage={t("part.files.none", "No files yet.")}
        rowActions={(row) => (
          <>
            <Button variant="ghost" size="sm" onClick={() => void open(row)} title={t("part.files.open", "Open")}>
              <ExternalLink className="size-3.5" />
            </Button>
            {!readOnly && !row.is_current && (
              <Button variant="ghost" size="sm" onClick={() => void makeCurrent(row)} title={t("part.files.make-current", "Make current")}>
                <CheckCircle2 className="size-3.5" />
              </Button>
            )}
          </>
        )}
        editor={
          readOnly
            ? undefined
            : {
                fields: {
                  kind: {
                    kind: "select",
                    get: (row) => row.kind,
                    set: (row, value) => ({ ...row, kind: value as PartFileKind }),
                    options: PART_FILE_KINDS.map((kind) => ({ value: kind, label: partFileKindLabel(t, kind) })),
                    required: true,
                  },
                  revision: {
                    kind: "select",
                    get: (row) => (row.bom_id == null ? "" : String(row.bom_id)),
                    set: (row, value) => ({ ...row, bom_id: value === "" ? null : Number(value) }),
                    options: (row) => revisionOptions(row.part_id),
                  },
                  note: { kind: "text", get: (row) => row.note, set: (row, value) => ({ ...row, note: value === "" ? null : value }) },
                },
                onSave: async (row) => {
                  await client.update(row);
                  await refresh();
                  toast.success(t("notification.saved", "Saved"));
                },
                onDelete: async (row) => {
                  await client.remove(row.id);
                  await refresh();
                  toast.success(t("notification.deleted", "Deleted"));
                },
                deleteConfirm: (row) => ({
                  title: t("part.files.delete", "Delete {{name}}?", { name: row.file_name }),
                  message: t("part.files.delete.message", "The file is moved aside in storage, not destroyed."),
                }),
              }
        }
      />
    </div>
  );
}
